import { computed, ref, watch } from 'vue';
import type { ComputedRef, Ref } from 'vue';
import entityPulseApi from '@/api/modules/entityPulseApi';
import type { EntityMonitoringPolicyItem } from '@/types/entityPulse';
import { normalizeTaxId } from '@/utils/formatters';

export interface EntityMonitoringView {
  isMonitored: boolean;
  policies: EntityMonitoringPolicyItem[];
  isLoading: boolean;
}

interface UseEntityMonitoringOptions {
  entityType: 'company' | 'person';
  /** Already the id the surfaces key on: company = 8-char CNPJ root, person = CPF. Null until resolved. */
  taxId: Ref<string | null | undefined> | ComputedRef<string | null | undefined>;
  /** Feature-gated: only fetch when the account/user may see Pulse. */
  enabled: Ref<boolean> | ComputedRef<boolean>;
}

/**
 * Monitoring status for the banner "Monitorado" icon: whether an active monitoring policy currently
 * enrolls the entity (real membership from the config, not events) + the policies enrolling it.
 *
 * Backed by Postgres (fast) and deliberately NOT cached: the answer must stay fresh so that
 * adding/removing the entity from a policy shows up on the next visit. State is local to the
 * composable instance and has no dependency on the recent-event-count call.
 */
export function useEntityMonitoringStatus(options: UseEntityMonitoringOptions) {
  const isMonitored = ref(false);
  const policies = ref<EntityMonitoringPolicyItem[]>([]);
  const isLoading = ref(false);

  const targetTaxId = computed(() => {
    if (!options.enabled.value) return null;
    return normalizeTaxId(options.taxId.value) || null;
  });

  const load = async (taxId: string) => {
    isLoading.value = true;
    try {
      const response = await entityPulseApi.getMonitoringStatus({ taxId, entityType: options.entityType });
      // Drop a response whose taxId is no longer the one we want (navigated away mid-flight).
      if (targetTaxId.value !== taxId) return;
      isMonitored.value = response.is_monitored;
      policies.value = response.policies;
    } catch (error) {
      console.error('Failed to load entity monitoring status:', error);
    } finally {
      if (targetTaxId.value === taxId) isLoading.value = false;
    }
  };

  watch(targetTaxId, (taxId) => {
    if (!taxId) {
      isMonitored.value = false;
      policies.value = [];
      isLoading.value = false;
      return;
    }
    void load(taxId);
  }, { immediate: true });

  return {
    view: computed<EntityMonitoringView>(() => ({
      isMonitored: isMonitored.value,
      policies: policies.value,
      isLoading: isLoading.value,
    })),
  };
}

/**
 * Count of all Pulse events of the entity — from its own monitoring plus other entities' monitoring —
 * for the count badge on the Pulse tab. Independent of the monitoring status call.
 */
export function useEntityRecentEventCount(options: UseEntityMonitoringOptions) {
  const recentEventCount = ref<number | null>(null);
  const isRecentEventsLoading = ref(false);

  const targetTaxId = computed(() => {
    if (!options.enabled.value) return null;
    return normalizeTaxId(options.taxId.value) || null;
  });

  const load = async (taxId: string) => {
    isRecentEventsLoading.value = true;

    try {
      const response = await entityPulseApi.getEntityEvents({
        taxId,
        limit: 1,
        offset: 0,
      });
      // Drop a response whose taxId is no longer the one we want (navigated away mid-flight).
      if (targetTaxId.value !== taxId) return;
      recentEventCount.value = response.pagination.total;
    } catch (error) {
      console.error('Failed to load recent entity pulse event count:', error);
    } finally {
      if (targetTaxId.value === taxId) isRecentEventsLoading.value = false;
    }
  };

  watch(targetTaxId, (taxId) => {
    recentEventCount.value = null;
    if (!taxId) {
      isRecentEventsLoading.value = false;
      return;
    }
    void load(taxId);
  }, { immediate: true });

  return { recentEventCount, isRecentEventsLoading };
}
